import { useState, useCallback } from 'react';
import { createMessageObject } from '../utils/storage.js';
import { generateAIResponse, generateConversationSummary } from '../utils/geminiApi.js';

export const useChat = ({ 
  currentConversationId, 
  currentConversation, 
  apiKey, 
  addMessageToConversation,
  updateConversationSummary,
  editMessageInConversation,
  setApiError,
  openApiKeyModal 
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // AI応答生成を停止
  const stopGeneration = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      setApiError('応答の生成が中止されました');
    }
  }, [abortController, setApiError]);

  // メッセージを送信
  const sendMessage = useCallback(async () => {
    if (!inputMessage.trim() || !currentConversationId || isLoading) return;

    // APIキーチェック
    if (!apiKey) {
      openApiKeyModal();
      return;
    }

    const userMessage = createMessageObject(inputMessage, 'user');
    
    if (!currentConversation) return;

    // 会話にメッセージを追加
    addMessageToConversation(currentConversationId, userMessage);
    setInputMessage('');
    setIsLoading(true);
    setApiError(null);

    // AbortControllerを作成
    const controller = new AbortController();
    setAbortController(controller);

    // AI応答を生成
    try {
      const aiResponse = await generateAIResponse(inputMessage, currentConversation.messages, apiKey, controller);
      const assistantMessage = createMessageObject(aiResponse, 'assistant');

      addMessageToConversation(currentConversationId, assistantMessage);

      // サマリー生成（メッセージが3つ以上かつサマリーがない場合）
      const updatedMessages = [...currentConversation.messages, userMessage, assistantMessage];
      if (updatedMessages.length >= 3 && !currentConversation.summary && apiKey) {
        generateConversationSummary(updatedMessages, apiKey, controller)
          .then(summary => {
            updateConversationSummary(currentConversationId, summary);
          })
          .catch(error => {
            console.error('Summary generation failed:', error);
          });
      }
    } catch (error) {
      console.error('Error generating response:', error);
      setApiError(error instanceof Error ? error.message : '応答の生成中にエラーが発生しました');
      
      // APIキーエラーの場合はモーダルを表示
      if (error instanceof Error && error.message.includes('APIキー')) {
        openApiKeyModal();
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  }, [inputMessage, currentConversationId, isLoading, apiKey, currentConversation, addMessageToConversation, updateConversationSummary, setApiError, openApiKeyModal]);

  // メッセージをコピー
  const copyMessage = useCallback((message) => {
    navigator.clipboard.writeText(message.content);
    setCopiedMessageId(message.id);
    setTimeout(() => setCopiedMessageId(null), 2000);
  }, []);

  // メッセージの編集を開始
  const startEditMessage = useCallback((message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }, []);

  // メッセージの編集をキャンセル
  const cancelEditMessage = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
  }, []);

  // メッセージを編集して保存
  const editMessage = useCallback(async (messageId, newContent) => {
    if (!newContent.trim() || !currentConversationId || isLoading) return;

    const result = editMessageInConversation(currentConversationId, messageId, newContent);
    if (!result) return;

    const { messagesBeforeEdit, editedMessage } = result;

    // 編集モードを終了
    setEditingMessageId(null);
    setEditingContent('');
    setIsLoading(true);
    setApiError(null);

    // AbortControllerを作成
    const controller = new AbortController();
    setAbortController(controller);

    // AIの新しい応答を生成（ユーザーメッセージの場合のみ）
    if (editedMessage.role === 'user') {
      try {
        const aiResponse = await generateAIResponse(newContent, messagesBeforeEdit, apiKey, controller);
        const assistantMessage = createMessageObject(aiResponse, 'assistant');
        addMessageToConversation(currentConversationId, assistantMessage);
      } catch (error) {
        console.error('Error generating response:', error);
        setApiError(error instanceof Error ? error.message : '応答の生成中にエラーが発生しました');
      }
    }
    
    setIsLoading(false);
    setAbortController(null);
  }, [currentConversationId, isLoading, editMessageInConversation, addMessageToConversation, apiKey, setApiError]);

  return {
    inputMessage,
    setInputMessage,
    isLoading,
    editingMessageId,
    editingContent,
    setEditingContent,
    copiedMessageId,
    sendMessage,
    stopGeneration,
    copyMessage,
    startEditMessage,
    cancelEditMessage,
    editMessage
  };
};